import typia from 'typia';
import type { FormattingAndLabelRules, LoadedGraphWithViewpointMode, } from '@ardoq/data-model';
import type { VisibleDataSummary, TypeSummary, FieldSummary, ColorFormatting, FormattingFilter, ListFieldFormatting, FormattingOperator, } from '@ardoq/api-types';
import { buildVisibleDataSummary } from 'canvas/aiFormatting/buildVisibleDataSummary';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { describeExpectedDateValue } from './dateValueShape';
import { describeExpectedNumberValue, describeInvalidColorRange, toNumberValue, } from './numberValueShape';
import { collectAvailableTagNames } from 'canvas/aiFormatting/collectAvailableTagNames';
import { collectComponentTypeNamesWithAncestors } from 'canvas/aiFormatting/collectComponentTypeNamesWithAncestors';
import { v4 as uuid } from 'uuid';
import { CommonDefaultFilterKeys, EMPTY, FieldFormattingVersion, FilterInfoOperator, FilterTypes, FormattingRuleOperator, isListFieldFormatting, isSystemFilter, SystemFilterType, type SystemFilterAttributes, } from '@ardoq/api-types';
import { calculateSystemFilters } from 'models/utils/filterUtils';
import { coerceComparator } from 'models/utils/coerceComparator';
import { applyConditionalFormattingAsOneHistoryEntry } from 'streams/perspectiveEditorData/applyFormatting';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import { VIEWPOINT_DOMAIN } from 'aiControlledCommands/viewpoint/domain';
import { WHEN_TO_USE, FORMATTING_CONTEXT_DESCRIPTION } from './descriptions';
import { filterOperations } from '@ardoq/core';
import { isEqual, isUndefined, omit, omitBy } from 'lodash';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { assignListColors, canSetTypeField, COMPONENT_BUILT_IN_FIELD_DEFS, getFormattingComparatorsForBuiltInField, isCompleteFormattingRule, isValidTypeRule, } from '@ardoq/perspectives';
import { filterTypeByApiFieldType, QueryBuilderFilterType, } from '@ardoq/query-builder';
import { formattingRulesToFormattingAttributes } from 'modelInterface/filters/formattingRulesToFormattingAttributes';
import { mapToPerspectiveEditorFormattingRule } from 'streams/perspectiveEditorData/mapToPerspectiveEditorFormattingRule';
import { fieldOps } from 'models/utils/fieldOps';
import type { AiControlledCommand, CommandExecutionState, } from 'aiControlledCommands/types';
import { toCommandRejection, type ShowName, } from 'aiControlledCommands/validationRejection';
// Omitted from the conditional-formatting schema: FE-managed housekeeping
// (`id`, `version`, `isCollapsed`) and `includeTime` (a label-formatting option
// that colour rules never read).
const MACHINE_FILTER_FIELDS = [
    'id',
    'version',
    'isCollapsed',
    'includeTime',
] as const satisfies readonly (keyof FormattingFilter)[];
type MachineFilterField = (typeof MACHINE_FILTER_FIELDS)[number];
// typia compiles the JSDoc here into the AI conditional-formatting tool's
// JSON schema, so these comments are LLM-facing — keep them accurate.
/**
 * A single conditional-formatting rule. The kind of entity it colours is encoded
 * by `affectComponent`, `affectReference`, and `type` together:
 * - component rule: `affectComponent: true`, `affectReference: false`, `type: "attribute"`;
 * - reference rule: `affectComponent: false`, `affectReference: true`, `type: "attribute"`;
 * - tag rule: `affectComponent: true`, `affectReference: true`, `type: "tag"` — matches every component and reference carrying the tag named in `value`. Set `isNegative: true` to invert it, so it matches every component and reference that does NOT carry the tag ("without tag X", "not tagged X"). `isNegative` is for tag rules only — never set it on other rules; negate those with a negated comparator such as `notEqual` or `notContains`. Leave it out otherwise.
 * - system-filter rule — greys out (dims) every component and reference that matches none of the other rules, so only the formatted entities stand out.
 *   Include it whenever the user asks to dim, grey out, fade, mute, de-emphasise, or wash out the irrelevant, non-matching, or remaining data, and whenever they ask for the formatted entities to stand out against the rest. It has no effect on its own — only add it alongside at least one component or reference rule. Leave it out when the user asks for colours only, and drop it from the rule set when the user asks to stop dimming or to restore the other entities' colours.
 *   It carries no condition of its own, so emit it verbatim as `{ "type": "system_filter", "affectComponent": true, "affectReference": true, "name": "0", "value": "", "color": "#D4DAE3" }`.
 */
// `affectsTypeName` is the legacy single-type scope, superseded by the plural
// `affectsTypeNames`. It is omitted from the schema so the assistant only ever
// composes the plural form, but it is NOT stripped from the rules at runtime, so
// an existing rule still scoped by the legacy field keeps that scope in context.
export type AiFormattingFilter = Omit<FormattingFilter, MachineFilterField | 'affectsTypeName' | 'isNegative'> & {
    isNegative?: boolean;
};
const toAiFormattingFilter = (filter: FormattingFilter | AiFormattingFilter): AiFormattingFilter => omitBy({
    ...omit(filter, MACHINE_FILTER_FIELDS),
    isNegative: filter.isNegative ? true : undefined,
    }, isUndefined) as AiFormattingFilter;
const TYPE_FIELD_DEFINITION = COMPONENT_BUILT_IN_FIELD_DEFS.find(({ id }) => id === CommonDefaultFilterKeys.TYPE);
const TYPE_FIELD_COMPARATORS: FormattingOperator[] = TYPE_FIELD_DEFINITION
    ? getFormattingComparatorsForBuiltInField(TYPE_FIELD_DEFINITION)
    : [];
// The comparator is read through `coerceComparator` because the filters coerce
// it on the way in: a type rule the AI sent as `equals` is stored as `idMatch`.
const isTypeFieldRule = (rule: AiFormattingFilter): boolean => {
    if (rule.name !== CommonDefaultFilterKeys.TYPE) {
        return false;
    }
    const comparator = coerceComparator(rule.name, rule.comparator);
    return (comparator !== undefined && TYPE_FIELD_COMPARATORS.includes(comparator));
};
// A "colour by type" rule encodes its type in `value` (name: "type",
// comparator: "idMatch", value: "<TypeName>"), but the type picker reads the
// scope (`affectsTypeNames`). The manual UI keeps both in sync; mirror the value
// into the scope here so the AI-produced rule matches that shape out of the box.
export const withColorByTypeScope = (rule: AiFormattingFilter): AiFormattingFilter => isTypeFieldRule(rule) &&
    !rule.affectsTypeNames?.length &&
    typeof rule.value === 'string' &&
    rule.value
    ? { ...rule, affectsTypeNames: [rule.value] }
    : rule;
// The filters match a tag rule on `name`, not `value`; the rule editor sets both
// to the tag, but the AI only fills `value`. Saving the rule the way the editor
// does gives it the same shape.
const withTagNameFromValue = (rule: AiFormattingFilter): AiFormattingFilter => {
    if (rule.type !== FilterTypes.TAG) {
        return rule;
    }
    const editorRule = mapToPerspectiveEditorFormattingRule(backfillMachineFields(rule));
    const [attributes] = editorRule
        ? formattingRulesToFormattingAttributes([editorRule])
        : [];
    return { ...rule, name: attributes?.name ?? rule.name };
};
// An is-empty rule takes no value, and the completeness check accepts only `null`
// for it, while the schema does not tell the AI to send one.
const withNullValueForIsEmpty = (rule: AiFormattingFilter): AiFormattingFilter => rule.comparator === FormattingRuleOperator.IS_EMPTY
    ? { ...rule, value: null }
    : rule;
// MACHINE_FILTER_FIELDS are stripped from the schema (see toAiFormattingFilter),
// so the AI payload carries none of them; backfill defaults to recover a valid
// FormattingFilter to apply.
const backfillMachineFields = (rule: AiFormattingFilter): FormattingFilter => ({
    ...rule,
    id: uuid(),
    isNegative: rule.isNegative ?? false,
    version: FieldFormattingVersion.NEW_CONDITIONAL_FORMATTING,
    isCollapsed: false,
    includeTime: false,
});
// A blank color marks the values assignListColors should fill in.
const withEveryOption = (current: ListFieldFormatting, options: string[]): ListFieldFormatting => {
    const undeclaredValues = Object.keys(current).filter(value => value !== EMPTY && !options.includes(value));
    return {
        ...Object.fromEntries([...options, ...undeclaredValues].map((value): [
            string,
            ColorFormatting
        ] => [
            value,
            current[value] ?? { color: '', isDisabled: false },
        ])),
        [EMPTY]: current[EMPTY],
    };
};
// A field with a predefined color map (`criticality`, `strategic_rating`, …) makes
// assignListColors replace a value's color outright, so restore the colors the AI
// picked and keep only the ones it assigned to the values added above.
const withColorsChosenByAi = (assignedFormatting: ListFieldFormatting, aiFormatting: ListFieldFormatting): ListFieldFormatting => ({
    ...assignedFormatting,
    ...Object.fromEntries(Object.entries(aiFormatting).filter(([, formatting]) => formatting?.color)),
});
// The AI only sees the values present in the visible data, so its color map covers
// those alone. A map that omits an option hides it from the rule editor and colors
// the entities carrying it with the "No value" color. Fill in every option the field
// declares, then color the added ones the way the rule editor does, avoiding the
// colors the other rules already use.
const isColorMapComparator = (comparator: FormattingOperator | undefined): boolean => comparator === undefined || comparator === FilterInfoOperator.EQUALS;
type ListFieldNormalizationState = Pick<CommandExecutionState, 'activeViewId' | 'globalFieldsByName'>;
const normalizeListFieldRule = (rule: AiFormattingFilter, rules: AiFormattingFilter[], { activeViewId, globalFieldsByName }: ListFieldNormalizationState): AiFormattingFilter => {
    if (!isColorMapComparator(rule.comparator)) {
        return rule;
    }
    if (!isListFieldFormatting(rule.value)) {
        return rule;
    }
    const field = globalFieldsByName[rule.name];
    const options = field ? fieldOps.getAllowedValues(field) : [];
    if (options.length === 0) {
        return rule;
    }
    const assignedFormatting = assignListColors(rule.name, withEveryOption(rule.value, options), rules, activeViewId);
    return {
        ...rule,
        value: withColorsChosenByAi(assignedFormatting, rule.value),
    };
};
// A `map` would offer every list-field rule the same free colors, so each rule is colored
// against the rules normalized before it — and against itself, so its own colors count as taken.
const normalizeListFieldRules = (state: ListFieldNormalizationState) => (normalizedRules: AiFormattingFilter[], rule: AiFormattingFilter, index: number, rules: AiFormattingFilter[]): AiFormattingFilter[] => [
    ...normalizedRules,
    normalizeListFieldRule(rule, [...normalizedRules, ...rules.slice(index)], state),
];
// The AI's payload passes typia schema validation (right shapes and types) but can still
// carry a rule with no real condition to test — e.g. an attribute rule missing its comparator,
// or an empty `value`. Such a rule is applied without error yet colours nothing, which is what
// makes the AI's reported success misleading.
const isEffectiveAiFormattingFilter = (filter: AiFormattingFilter): boolean => {
    if (filter.type === FilterTypes.ATTRIBUTE && !filter.name) {
        return false;
    }
    const editorRule = mapToPerspectiveEditorFormattingRule(backfillMachineFields(filter));
    return editorRule !== null && isCompleteFormattingRule(editorRule);
};
const hasSingleAffectedEntityKind = (filter: AiFormattingFilter): boolean => filter.affectComponent !== filter.affectReference;
type TypeNameSources = {
    summary: VisibleDataSummary;
    componentTypeNamesWithAncestors: string[];
};
const toTypeNamesSet = (types: TypeSummary[]): Set<string> => new Set(types.map(({ typeName }) => typeName));
const isAffectedTypeVisible = (filter: AiFormattingFilter, { summary, componentTypeNamesWithAncestors }: TypeNameSources): boolean => {
    const editorRule = mapToPerspectiveEditorFormattingRule(backfillMachineFields(filter));
    return (editorRule !== null &&
        isValidTypeRule(editorRule, {
            componentTypeNamesSet: new Set([
                ...toTypeNamesSet(summary.componentTypes),
                ...componentTypeNamesWithAncestors,
            ]),
            referenceTypeNamesSet: toTypeNamesSet(summary.referenceTypes),
        }));
};
const isFilterVisibleInRuleEditor = (filter: AiFormattingFilter, typeNameSources: TypeNameSources): boolean => {
    if (filter.type !== FilterTypes.ATTRIBUTE) {
        return true;
    }
    if (!hasSingleAffectedEntityKind(filter)) {
        return false;
    }
    return (filter.name !== CommonDefaultFilterKeys.TYPE ||
        isAffectedTypeVisible(filter, typeNameSources));
};
const projectSystemFilter = ({ affectComponent = false, affectReference = false, name, value = '', color = '', }: SystemFilterAttributes): AiFormattingFilter => ({
    type: FilterTypes.SYSTEM_FILTER,
    affectComponent,
    affectReference,
    name,
    value,
    color,
});
const isDefaultColorSystemFilter = (rule: AiFormattingFilter): boolean => isSystemFilter(rule) && rule.name === SystemFilterType.DEFAULT_COLOR;
const rebuildDimmingFilter = (rules: AiFormattingFilter[]): AiFormattingFilter[] => {
    const colourRules = rules.filter(rule => !isSystemFilter(rule));
    const systemFilters = calculateSystemFilters({
        conditionalFormattingRules: colourRules,
        shouldGreyOut: rules.some(isDefaultColorSystemFilter),
    }).filter(isSystemFilter);
    return [...colourRules, ...systemFilters.map(projectSystemFilter)];
};
// The filters coerce an attribute rule's comparator when they store it, so the
// rules we hand to `isApplied` have to be coerced the same way to compare equal
// to what reads back. This mirrors the coercion in `Filter.set`; it is applied
// only to the comparison, never to what we execute, so each side converts once.
const coerceRuleComparator = (rule: AiFormattingFilter): AiFormattingFilter => {
    if (rule.type !== FilterTypes.ATTRIBUTE || !rule.name)
        return rule;
    if (rule.comparator === FilterInfoOperator.EQUALS && rule.value === null) {
        return rule;
    }
    const { comparator, ...rest } = rule;
    const coercedComparator = coerceComparator(rule.name, comparator);
    if (!coercedComparator)
        return rest;
    return { ...rest, comparator: coercedComparator };
};
const normalizeAiRule = (rule: AiFormattingFilter): AiFormattingFilter => withTagNameFromValue(withNullValueForIsEmpty(withColorByTypeScope(rule)));
const withNumberValue = (summary: VisibleDataSummary) => (rule: AiFormattingFilter): AiFormattingFilter => hasFieldOfFilterType(rule, getTargetTypes(rule, summary), QueryBuilderFilterType.NUMBER)
    ? { ...rule, value: toNumberValue(rule.comparator, rule.value) }
    : rule;
const normalizeConditionalFormattingRules = (rules: AiFormattingFilter[], summary: VisibleDataSummary, state: ListFieldNormalizationState): AiFormattingFilter[] => rebuildDimmingFilter(rules
    .map(normalizeAiRule)
    .map(withNumberValue(summary))
    .reduce<AiFormattingFilter[]>(normalizeListFieldRules(state), [])).map(toAiFormattingFilter);
/**
 * The rules as the filters will hold them once applied: the comparator coercion
 * `Filter.set` performs, mirrored here so `isApplied` compares like with like.
 */
const asStoredByTheFilters = (rules: AiFormattingFilter[]): AiFormattingFilter[] => rules.map(coerceRuleComparator);
export type InvalidConditionalFormattingRule = {
    kind: 'comparator';
    fieldName: string;
    comparator: FormattingOperator;
    allowedComparators: FormattingOperator[];
    } | {
    kind: 'unavailableField';
    fieldName: string ;
} | {
    kind: 'multiTypeColorRule';
    typeNames: string[] ;
} | {
    kind: 'incompleteRule';
    fieldName: string ;
} | {
    kind: 'invalidDateValue';
    fieldName: string;
    comparator: FormattingOperator;
    expected: string;
    } | {
    kind: 'invisibleRule' ;
} | {
    kind: 'singleValueListRule';
    fieldName: string ;
} | {
    kind: 'multiSelectColorMapRule';
    fieldName: string;
    allowedComparators: FormattingOperator[];
    } | {
    kind: 'invalidNumberValue';
    fieldName: string;
    comparator: FormattingOperator;
    expected: string;
    } | {
    kind: 'invalidColorRange';
    fieldName: string;
    problem: string ;
} | {
    kind: 'invertedAttributeRule';
    fieldName: string ;
} | {
    kind: 'unavailableTag';
    tagName: string;
    availableTags: string[];
};
export const showInvalidRuleNames = (invalidRule: InvalidConditionalFormattingRule, showName: ShowName): InvalidConditionalFormattingRule => {
    switch (invalidRule.kind) {
        case 'multiTypeColorRule':
            return {
                ...invalidRule,
                typeNames: invalidRule.typeNames.map(showName),
            };
        case 'unavailableTag':
            return {
                ...invalidRule,
                tagName: showName(invalidRule.tagName),
                availableTags: invalidRule.availableTags.map(showName),
            };
        case 'invisibleRule':
            return invalidRule;
        default:
            return { ...invalidRule, fieldName: showName(invalidRule.fieldName) };
    }
};
const getTargetTypes = (rule: AiFormattingFilter, summary: VisibleDataSummary): TypeSummary[] => {
    const allTargetTypes = [
        ...(rule.affectComponent ? summary.componentTypes : []),
        ...(rule.affectReference ? summary.referenceTypes : []),
    ];
    if (!rule.affectsTypeNames?.length) {
        return allTargetTypes;
    }
    const requestedTypeNames = new Set(rule.affectsTypeNames);
    return allTargetTypes.filter(type => requestedTypeNames.has(type.typeName));
};
const findFieldsByName = (rule: AiFormattingFilter, targetTypes: TypeSummary[]): FieldSummary[] => targetTypes.flatMap(type => type.fields.filter(field => field.name === rule.name));
const hasFieldOnTypes = (rule: AiFormattingFilter, targetTypes: TypeSummary[]): boolean => findFieldsByName(rule, targetTypes).length > 0;
// A comparator must be allowed for the field on every targeted type that has
// it, and at least one targeted type must have it.
const isValidComparatorRule = (rule: AiFormattingFilter, comparator: FormattingOperator, targetTypes: TypeSummary[]): boolean => {
    const fields = findFieldsByName(rule, targetTypes);
    return (fields.length > 0 &&
        fields.every(field => field.allowedComparators.includes(comparator)));
};
const isListFieldColorMapRule = (rule: AiFormattingFilter): boolean => isColorMapComparator(rule.comparator) && isListFieldFormatting(rule.value);
const FILTER_TYPE_BY_API_FIELD_TYPE = filterTypeByApiFieldType(true);
const hasFilterType = (field: FieldSummary, filterType: QueryBuilderFilterType): boolean => FILTER_TYPE_BY_API_FIELD_TYPE[field.type] === filterType;
const hasFieldOfFilterType = (rule: AiFormattingFilter, targetTypes: TypeSummary[], filterType: QueryBuilderFilterType): boolean => findFieldsByName(rule, targetTypes).some(field => hasFilterType(field, filterType));
const isMultiSelectColorMapRule = (rule: AiFormattingFilter, targetTypes: TypeSummary[]): boolean => isListFieldFormatting(rule.value) &&
    hasFieldOfFilterType(rule, targetTypes, QueryBuilderFilterType.SELECT_MULTIPLE);
const isOffUiListFieldRule = (rule: AiFormattingFilter, targetTypes: TypeSummary[]): boolean => !isListFieldColorMapRule(rule) &&
    hasFieldOfFilterType(rule, targetTypes, QueryBuilderFilterType.SELECT);
const getAllowedComparatorsForRule = (rule: AiFormattingFilter, targetTypes: TypeSummary[]): FormattingOperator[] => {
    if (targetTypes.length === 0) {
        return [];
    }
    if (rule.name === CommonDefaultFilterKeys.TYPE) {
        return TYPE_FIELD_COMPARATORS;
    }
    return [
        ...new Set(targetTypes.flatMap(type => {
            const field = type.fields.find(field => field.name === rule.name);
            return field?.allowedComparators ?? [];
        })),
    ];
};
const toComparatorViolation = (rule: AiFormattingFilter, comparator: FormattingOperator, summary: VisibleDataSummary): InvalidConditionalFormattingRule => ({
    kind: 'comparator',
    fieldName: rule.name,
    comparator,
    allowedComparators: getAllowedComparatorsForRule(rule, getTargetTypes(rule, summary)),
});
const findTypeFieldViolation = (rule: AiFormattingFilter, summary: VisibleDataSummary): InvalidConditionalFormattingRule | null => {
    if (rule.comparator === undefined) {
        return { kind: 'unavailableField', fieldName: rule.name };
    }
    if (!isTypeFieldRule(rule)) {
        return toComparatorViolation(rule, rule.comparator, summary);
    }
    if (canSetTypeField(rule.name, rule.affectsTypeNames ?? null)) {
        return null;
    }
    return (rule.affectsTypeNames?.length ?? 0) > 1
        ? { kind: 'multiTypeColorRule', typeNames: rule.affectsTypeNames ?? [] }
        : { kind: 'incompleteRule', fieldName: rule.name };
};
const findDateValueViolation = (rule: AiFormattingFilter): InvalidConditionalFormattingRule | null => {
    if (rule.comparator === undefined) {
        return null;
    }
    const expected = describeExpectedDateValue(rule.comparator, rule.value);
    return expected === null
        ? null
        : {
            kind: 'invalidDateValue',
            fieldName: rule.name,
            comparator: rule.comparator,
            expected,
        };
};
const findFieldViolation = (rule: AiFormattingFilter, { summary }: TypeNameSources): InvalidConditionalFormattingRule | null => {
    if (rule.name === CommonDefaultFilterKeys.TYPE) {
        return findTypeFieldViolation(rule, summary);
    }
    const targetTypes = getTargetTypes(rule, summary);
    const { comparator } = rule;
    if (isMultiSelectColorMapRule(rule, targetTypes)) {
        return {
            kind: 'multiSelectColorMapRule',
            fieldName: rule.name,
            allowedComparators: getAllowedComparatorsForRule(rule, targetTypes),
        };
    }
    if (!hasFieldOnTypes(rule, targetTypes)) {
        return { kind: 'unavailableField', fieldName: rule.name };
    }
    if (isOffUiListFieldRule(rule, targetTypes)) {
        return { kind: 'singleValueListRule', fieldName: rule.name };
    }
    if (comparator !== undefined &&
        !isValidComparatorRule(rule, comparator, targetTypes)) {
        return toComparatorViolation(rule, comparator, summary);
    }
    return findValueViolation(rule, targetTypes);
};
const findNumberValueViolation = (rule: AiFormattingFilter, targetTypes: TypeSummary[]): InvalidConditionalFormattingRule | null => {
    if (rule.comparator === undefined ||
        !hasFieldOfFilterType(rule, targetTypes, QueryBuilderFilterType.NUMBER)) {
        return null;
    }
    const expected = describeExpectedNumberValue(rule.name, rule.comparator, rule.value);
    if (expected !== null) {
        return {
            kind: 'invalidNumberValue',
            fieldName: rule.name,
            comparator: rule.comparator,
            expected,
        };
    }
    const problem = describeInvalidColorRange(rule.comparator, rule.value);
    return problem === null
        ? null
        : { kind: 'invalidColorRange', fieldName: rule.name, problem };
};
const findValueViolation = (rule: AiFormattingFilter, targetTypes: TypeSummary[]): InvalidConditionalFormattingRule | null => findCompletenessViolation(rule) ??
    findNumberValueViolation(rule, targetTypes);
const findCompletenessViolation = (rule: AiFormattingFilter): InvalidConditionalFormattingRule | null => {
    if (!isEffectiveAiFormattingFilter(rule)) {
        return { kind: 'incompleteRule', fieldName: rule.name };
    }
    return findDateValueViolation(rule);
};
const isNotSystemFilter = (rule: AiFormattingFilter): boolean => rule.type !== FilterTypes.SYSTEM_FILTER;
const findTagViolation = (rule: AiFormattingFilter, availableTagNames: string[]): InvalidConditionalFormattingRule | null => {
    if (!isEffectiveAiFormattingFilter(rule)) {
        return { kind: 'incompleteRule', fieldName: 'tag' };
    }
    return typeof rule.value === 'string' &&
        availableTagNames.includes(rule.value)
        ? null
        : {
            kind: 'unavailableTag',
            tagName: String(rule.value),
            availableTags: availableTagNames,
        };
};
const findInversionViolation = (rule: AiFormattingFilter): InvalidConditionalFormattingRule | null => rule.isNegative === true
    ? { kind: 'invertedAttributeRule', fieldName: rule.name }
    : null;
const findContentViolation = (rule: AiFormattingFilter, context: ApplyConditionalFormattingContext): InvalidConditionalFormattingRule | null => {
    if (rule.type === FilterTypes.TAG) {
        return findTagViolation(rule, context.availableTagNames);
    }
    return findInversionViolation(rule) ?? findFieldViolation(rule, context);
};
const findVisibilityViolation = (rule: AiFormattingFilter, typeNameSources: TypeNameSources): InvalidConditionalFormattingRule | null => isFilterVisibleInRuleEditor(rule, typeNameSources)
    ? null
    : { kind: 'invisibleRule' };
const findRuleViolation = (rule: AiFormattingFilter, context: ApplyConditionalFormattingContext): InvalidConditionalFormattingRule | null => findContentViolation(rule, context) ?? findVisibilityViolation(rule, context);
// Runtime/semantic validation. `typia.is` checks only payload shape; this
// additionally checks that a rule's field, comparator, scope and visibility are
// ones the settings panel supports. Rules the view already stores are left alone.
// Returns the first rule that fails, or `null` when every rule is valid.
const validateApplyConditionalFormattingPayload = (payload: ApplyConditionalFormattingPayload, context: ApplyConditionalFormattingContext): InvalidConditionalFormattingRule | null => payload.rules
    .filter(isNotSystemFilter)
    .filter(rule => !isStoredRule(context.currentRules, rule))
    .map(normalizeAiRule)
    .map(rule => findRuleViolation(rule, context))
    .find(violation => violation !== null) ?? null;
const EXPLAIN_LIMITATION_INSTRUCTION = 'Explain the limitation and suggest an alternative if one is available.';
const buildComparatorReason = ({ fieldName, comparator, allowedComparators, }: Extract<InvalidConditionalFormattingRule, {
    kind: 'comparator' ;
}>): string => {
    return allowedComparators.length
        ? `Invalid formatting: "${comparator}" is not supported for "${fieldName}". ` +
            `This field supports: ${allowedComparators.join(', ')}. ` +
            'Check whether the requested formatting can be expressed with a supported condition. ' +
            'If it cannot, explain the limitation and suggest an alternative.'
        : `Invalid formatting: "${comparator}" is not supported for "${fieldName}". ` +
            `This field has no supported comparators in the selected scope. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
};
const buildUnavailableTagReason = ({ tagName, availableTags, }: Extract<InvalidConditionalFormattingRule, {
    kind: 'unavailableTag' ;
}>): string => availableTags.length
    ? `Invalid formatting: the tag "${tagName}" does not exist. ` +
        `Available tags: ${availableTags.join(', ')}. ` +
        'Use one of them, or explain that the requested tag does not exist.'
    : `Invalid formatting: the tag "${tagName}" does not exist, and there are no tags to choose from. ` +
        'Explain the limitation to the user.';
const buildReasonMessage = (invalidRule: InvalidConditionalFormattingRule): string => {
    switch (invalidRule.kind) {
        case 'comparator':
            return buildComparatorReason(invalidRule);
        case 'unavailableField':
            return `Invalid formatting: "${invalidRule.fieldName}" is not available on the entities in the selected scope. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'multiTypeColorRule':
            return `Invalid formatting: a colour-by-type rule can target only one type, but this rule targets ${invalidRule.typeNames.join(', ')}. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'invalidDateValue':
            return `Invalid formatting: "${invalidRule.comparator}" on "${invalidRule.fieldName}" needs a value that is ${invalidRule.expected}. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'incompleteRule':
            return `Invalid formatting: the rule for "${invalidRule.fieldName}" has no value to compare against. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'invertedAttributeRule':
            return `Invalid formatting: only tag rules can be inverted with "isNegative". To negate the condition on "${invalidRule.fieldName}", use a negated comparator such as "${FormattingRuleOperator.NOT_EQUAL}" or "${FormattingRuleOperator.NOT_CONTAINS}" instead. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'invisibleRule':
            return 'A new or modified component or reference rule must affect exactly one of them, and a type rule must name a type of that kind listed in the summary or in componentTypeNamesWithAncestors.';
        case 'singleValueListRule':
            return `Invalid formatting: "${invalidRule.fieldName}" is a list field, and its colours are set as a single list-field colour map, not as separate rules per value. Colour the field with ONE rule whose value is a list-field colour map covering every option of the field. A subset of the options cannot be coloured on its own. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'multiSelectColorMapRule':
            return `Invalid formatting: "${invalidRule.fieldName}" is a multi-select field, and it cannot be coloured with a list-field colour map. Use a rule with a comparator (${invalidRule.allowedComparators.join(', ')}), a "value" that is an array of the option names sharing the colour (or a single option name), and a "color". ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'invalidNumberValue':
            return `Invalid formatting: "${invalidRule.comparator}" on "${invalidRule.fieldName}" needs a value that is ${invalidRule.expected}. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'invalidColorRange':
            return `Invalid formatting: the colour range on "${invalidRule.fieldName}" is not valid: ${invalidRule.problem}. ${EXPLAIN_LIMITATION_INSTRUCTION}`;
        case 'unavailableTag':
            return buildUnavailableTagReason(invalidRule);
        default:
            return exhaustiveCheck(invalidRule);
    }
};
// typia compiles the JSDoc here into the AI conditional-formatting tool's
// JSON schema, so these comments are LLM-facing — keep them accurate.
export type ApplyConditionalFormattingPayload = {
    /**
     * CHOOSE THE RULE SHAPE FIRST. To colour a list field by its values (e.g.
     * "highlight by Strategic Rating"), use a SINGLE rule whose `value` is a
     * list-field colour map (`ListFieldFormatting`). To colour a numeric field by a
     * gradient (e.g. "shade by cost"), use a SINGLE rule whose `value` is a colour
     * range (`ColorRangeFormatting`). To colour by a single condition, use a
     * `comparator` and `color`. A rule cannot combine conditions with logical AND/OR.
     *
     * The result is the COMPLETE set of conditional-formatting rules to apply to
     * the view, not a delta. By default keep every rule from `currentRules`
     * (provided in the context) and append the new ones; drop or change an existing
     * rule only when the user explicitly asks; an empty array clears all
     * formatting. Rules are independent and evaluated top-to-bottom; the first rule
     * that matches a given entity decides its colour. These rules set colour only —
     * not labels, icons, or grouping.
     *
     * DECIDE ON DIMMING TOO. A request to dim, grey out, fade, or mute the
     * irrelevant data — or to make the matching entities stand out against the
     * rest — is not a colour condition: it is the system-filter rule documented on
     * the rule type. Append that rule to this array alongside the colour rules
     * whenever the request carries that sense, and keep it in the array for as
     * long as the view should stay dimmed.
     *
     * Build each rule from the `summary` in the context: prefer the exact type,
     * field, and value the user names; when an exact match is not present, use the
     * closest match that is (users often do not know the exact field names), taking
     * the real values from `topValues`. Only when there is no reasonable match,
     * reply that you can't do that and explain the reason. Use the summary's
     * numeric and date distributions to choose sensible thresholds.
     */
    rules: AiFormattingFilter[];
};
type ApplyConditionalFormattingContext = TypeNameSources & {
    currentRules: AiFormattingFilter[];
    availableTagNames: string[];
};
const isStoredRule = (storedRules: AiFormattingFilter[], rule: AiFormattingFilter): boolean => {
    const projectedRule = toAiFormattingFilter(rule);
    return storedRules.some(stored => isEqual(stored, projectedRule));
};
const executeResolvedApplyConditionalFormatting = ({ rules, }: ApplyConditionalFormattingPayload): void => {
    applyConditionalFormattingAsOneHistoryEntry(rules.map(backfillMachineFields));
};
const isApplyConditionalFormattingContext = (context: unknown): context is ApplyConditionalFormattingContext => (() => { const _iv7 = new Set(["Checkbox", "Date", "DateOnlyRange", "DateTime", "DateTimeRange", "Email", "File", "List", "Number", "SelectMultipleList", "Text", "TextArea", "Url", "User"]); const _iv9 = new Set(["idMatch", "dateMatch", "dateEarlier", "dateLater", "boolean", "equals", "lessThan", "greaterThan", "contains", "dateRangeContains", "dateRangeDoesNotContain", "dateRangeBefore", "dateRangeAfter", "colorRange", "idDoesNotMatch", "notEqual", "notContains", "notBoolean", "greaterThanOrEqualTo", "lessThanOrEqualTo", "between", "notBetween", "dateBetween", "dateNotBetween", "dateNotEqual", "dateOnOrBefore", "dateOnOrAfter", "dateRangeOnOrBefore", "dateRangeOnOrAfter", "dateRangeBetween", "relativeDate", "isEmpty", "isNotEmpty", "listContains", "listDoesNotContain"]); const _iv11 = new Set(["idMatch", "dateMatch", "dateEarlier", "dateLater", "boolean", "equals", "lessThan", "greaterThan", "contains", "dateRangeContains", "dateRangeDoesNotContain", "dateRangeBefore", "dateRangeAfter", "colorRange", "idDoesNotMatch", "notEqual", "notContains", "notBoolean", "greaterThanOrEqualTo", "lessThanOrEqualTo", "between", "notBetween", "dateBetween", "dateNotBetween", "dateNotEqual", "dateOnOrBefore", "dateOnOrAfter", "dateRangeOnOrBefore", "dateRangeOnOrAfter", "dateRangeBetween", "relativeDate", "isEmpty", "isNotEmpty", "listContains", "listDoesNotContain"]); const _ip0 = (input: any) => {
    const array = input;
    const tuplePredicators = [
        [
            (top: any[]): any => top.length === 2 && "number" === typeof top[0] && "number" === typeof top[1],
            (entire: any[]): any => entire.length === 2 && "number" === typeof entire[0] && "number" === typeof entire[1]
        ] as const
    ];
    for (const pred of tuplePredicators)
        if (pred[0](array))
            return pred[1](array);
    const top = input[0];
    if (0 === input.length)
        return true;
    const arrayPredicators = [
        [
            (top: any[]): any => "string" === typeof top,
            (entire: any[]): any => entire.every((elem: any) => "string" === typeof elem)
        ] as const
    ];
    const passed = arrayPredicators.filter((pred: any) => pred[0](top));
    if (1 === passed.length)
        return passed[0]![1](array);
    else if (1 < passed.length)
        for (const pred of passed)
            if (array.every((value: any) => true === pred[0](value)))
                return pred[1](array);
    return false;
}; const _io0 = (input: any): boolean => "object" === typeof input.summary && null !== input.summary && _io1(input.summary) && (Array.isArray(input.componentTypeNamesWithAncestors) && input.componentTypeNamesWithAncestors.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.currentRules) && input.currentRules.every((elem: any) => "object" === typeof elem && null !== elem && _io10(elem))) && (Array.isArray(input.availableTagNames) && input.availableTagNames.every((elem: any) => "string" === typeof elem)); const _io1 = (input: any): boolean => Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem)) && (Array.isArray(input.referenceTypes) && input.referenceTypes.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem))); const _io2 = (input: any): boolean => "string" === typeof input.typeName && "number" === typeof input.instanceCount && (Array.isArray(input.fields) && input.fields.every((elem: any) => "object" === typeof elem && null !== elem && _io3(elem))); const _io3 = (input: any): boolean => "string" === typeof input.name && true === _iv7.has(input.type) && "number" === typeof input.presentCount && "number" === typeof input.nullCount && ("object" === typeof input.distribution && null !== input.distribution && _iu0(input.distribution)) && (Array.isArray(input.allowedComparators) && input.allowedComparators.every((elem: any) => true === _iv9.has(elem))); const _io4 = (input: any): boolean => "enum" === input.kind && (Array.isArray(input.topValues) && input.topValues.every((elem: any) => "object" === typeof elem && null !== elem && _io5(elem))) && "number" === typeof input.distinctCount; const _io5 = (input: any): boolean => "string" === typeof input.value && "number" === typeof input.count; const _io6 = (input: any): boolean => "numeric" === input.kind && "number" === typeof input.min && "number" === typeof input.max && "number" === typeof input.mean; const _io7 = (input: any): boolean => "date" === input.kind && "string" === typeof input.min && "string" === typeof input.max; const _io8 = (input: any): boolean => "text" === input.kind && "number" === typeof input.avgLength && "number" === typeof input.blankCount; const _io9 = (input: any): boolean => "opaque" === input.kind; const _io10 = (input: any): boolean => "boolean" === typeof input.affectComponent && "boolean" === typeof input.affectReference && (undefined === input.comparator || true === _iv11.has(input.comparator)) && "string" === typeof input.name && ("attribute" === input.type || "tag" === input.type || "system_filter" === input.type || "date_range" === input.type || "refLabel" === input.type || "compLabel" === input.type) && (undefined !== input.value && (null === input.value || "string" === typeof input.value || "number" === typeof input.value || "boolean" === typeof input.value || (Array.isArray(input.value) && (_ip0(input.value) || false) || "object" === typeof input.value && null !== input.value && _iu1(input.value)))) && "string" === typeof input.color && (undefined === input.affectsTypeNames || Array.isArray(input.affectsTypeNames) && input.affectsTypeNames.every((elem: any) => "string" === typeof elem)) && (undefined === input.isNegative || "boolean" === typeof input.isNegative); const _io11 = (input: any): boolean => "object" === typeof input["No value"] && null !== input["No value"] && _io12(input["No value"]) && Object.keys(input).every((key: any) => {
    if (["No value"].some((prop: any) => key === prop))
        return true;
    const value = input[key];
    if (undefined === value)
        return true;
    return undefined === value || "object" === typeof value && null !== value && _io12(value);
}); const _io12 = (input: any): boolean => "string" === typeof input.color && "boolean" === typeof input.isDisabled; const _io13 = (input: any): boolean => "color-range" === input.kind && ("number" === input.mode || "percent" === input.mode || "percentile" === input.mode) && ("object" === typeof input.min && null !== input.min && _io14(input.min)) && ("object" === typeof input.mid && null !== input.mid && _io15(input.mid)) && ("object" === typeof input.max && null !== input.max && _io14(input.max)); const _io14 = (input: any): boolean => (null === input.value || "number" === typeof input.value) && "string" === typeof input.color; const _io15 = (input: any): boolean => (null === input.value || undefined === input.value || "number" === typeof input.value) && "string" === typeof input.color; const _io16 = (input: any): boolean => ("this" === input.period || "last" === input.period || "next" === input.period) && ("day" === input.unit || "week" === input.unit || "month" === input.unit || "quarter" === input.unit || "year" === input.unit); const _io17 = (input: any): boolean => ("inTheNext" === input.period || "inThePast" === input.period) && "number" === typeof input.amount && ("day" === input.unit || "week" === input.unit || "month" === input.unit || "quarter" === input.unit || "year" === input.unit); const _iu0 = (input: any): any => (() => {
    if ("enum" === input.kind)
        return _io4(input);
    else if ("numeric" === input.kind)
        return _io6(input);
    else if ("date" === input.kind)
        return _io7(input);
    else if ("text" === input.kind)
        return _io8(input);
    else if ("opaque" === input.kind)
        return _io9(input);
    else
        return false;
})(); const _iu1 = (input: any): any => (() => {
    if (undefined !== input["No value"])
        return _io11(input);
    else if (undefined !== input.kind)
        return _io13(input);
    else if ("this" === input.period || "last" === input.period || "next" === input.period)
        return _io16(input);
    else if ("inTheNext" === input.period || "inThePast" === input.period)
        return _io17(input);
    else
        return false;
})(); return (input: any): input is ApplyConditionalFormattingContext => "object" === typeof input && null !== input && _io0(input); })()(context);
export const buildConditionalFormattingCommand = (loadedGraph: LoadedGraphWithViewpointMode, { rules }: FormattingAndLabelRules): AiControlledCommand<typeof conditionalFormattingCommandDefinition> => ({
    ...conditionalFormattingCommandDefinition,
    context: {
        summary: buildVisibleDataSummary(loadedGraph),
        currentRules: rules
            .filter(filterOperations.isStrictlyFormattingFilter)
            .map(toAiFormattingFilter),
        componentTypeNamesWithAncestors: collectComponentTypeNamesWithAncestors(loadedGraph),
        availableTagNames: collectAvailableTagNames(loadedGraph),
    },
});
export const conditionalFormattingCommandDefinition = defineAiControlledCommand<ApplyConditionalFormattingPayload, ApplyConditionalFormattingContext>({
    name: AiControlledCommandId.APPLY_CONDITIONAL_FORMATTING,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                ApplyConditionalFormattingPayload: {
                    type: "object",
                    properties: {
                        rules: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/AiFormattingFilter"
                            },
                            description: "CHOOSE THE RULE SHAPE FIRST. To colour a list field by its values (e.g.\n\"highlight by Strategic Rating\"), use a SINGLE rule whose `value` is a\nlist-field colour map (`ListFieldFormatting`). To colour a numeric field by a\ngradient (e.g. \"shade by cost\"), use a SINGLE rule whose `value` is a colour\nrange (`ColorRangeFormatting`). To colour by a single condition, use a\n`comparator` and `color`. A rule cannot combine conditions with logical AND/OR.\n\nThe result is the COMPLETE set of conditional-formatting rules to apply to\nthe view, not a delta. By default keep every rule from `currentRules`\n(provided in the context) and append the new ones; drop or change an existing\nrule only when the user explicitly asks; an empty array clears all\nformatting. Rules are independent and evaluated top-to-bottom; the first rule\nthat matches a given entity decides its colour. These rules set colour only \u2014\nnot labels, icons, or grouping.\n\nDECIDE ON DIMMING TOO. A request to dim, grey out, fade, or mute the\nirrelevant data \u2014 or to make the matching entities stand out against the\nrest \u2014 is not a colour condition: it is the system-filter rule documented on\nthe rule type. Append that rule to this array alongside the colour rules\nwhenever the request carries that sense, and keep it in the array for as\nlong as the view should stay dimmed.\n\nBuild each rule from the `summary` in the context: prefer the exact type,\nfield, and value the user names; when an exact match is not present, use the\nclosest match that is (users often do not know the exact field names), taking\nthe real values from `topValues`. Only when there is no reasonable match,\nreply that you can't do that and explain the reason. Use the summary's\nnumeric and date distributions to choose sensible thresholds."
                        }
                    },
                    required: [
                        "rules"
                    ]
                },
                AiFormattingFilter: {
                    type: "object",
                    properties: {
                        affectComponent: {
                            type: "boolean",
                            description: "`true` if the rule colours components."
                        },
                        affectReference: {
                            type: "boolean",
                            description: "`true` if the rule colours references. A tag rule sets both `affectComponent` and `affectReference` to `true` with `type: \"tag\"`."
                        },
                        comparator: {
                            oneOf: [
                                {
                                    "const": "idMatch",
                                    description: "Exact match on an id \u2014 the entity's id or the `type`\nfield (matched against a type name). `value`: a\nnon-empty string."
                                },
                                {
                                    "const": "dateMatch",
                                    description: "Matches when the date field equals the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"`."
                                },
                                {
                                    "const": "dateEarlier",
                                    description: "Matches when the date field is before the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"`."
                                },
                                {
                                    "const": "dateLater",
                                    description: "Matches when the date field is after the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"`."
                                },
                                {
                                    "const": "boolean",
                                    description: "Matches when the checkbox field is ticked. `value`: the literal `true`."
                                },
                                {
                                    "const": "equals",
                                    description: "Matches when the field equals the rule's `value`.\n\nTo colour a list/select field by its values, use ONE rule for the whole\nfield whose `value` is a list-field colour map (`ListFieldFormatting`) \u2014\nnever one rule per value. For any other field, `value` is a string or a\nnumber."
                                },
                                {
                                    "const": "lessThan",
                                    description: "Matches when the numeric field is less than the rule's `value`. `value`: a number."
                                },
                                {
                                    "const": "greaterThan",
                                    description: "Matches when the numeric field is greater than the rule's `value`. `value`: a number."
                                },
                                {
                                    "const": "contains",
                                    description: "Matches when the text field contains the rule's `value` as a substring. `value`: a string."
                                },
                                {
                                    "const": "dateRangeContains",
                                    description: "Matches when the date-range field contains the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"` or a relative-date object such as `{ \"period\": \"this\", \"unit\": \"month\" }`."
                                },
                                {
                                    "const": "dateRangeDoesNotContain",
                                    description: "Matches when the date-range field does not contain the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"` or a relative-date object such as `{ \"period\": \"this\", \"unit\": \"month\" }`."
                                },
                                {
                                    "const": "dateRangeBefore",
                                    description: "Matches when the date-range field lies entirely before the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"` or a relative-date object such as `{ \"period\": \"this\", \"unit\": \"month\" }`."
                                },
                                {
                                    "const": "dateRangeAfter",
                                    description: "Matches when the date-range field lies entirely after the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"` or a relative-date object such as `{ \"period\": \"this\", \"unit\": \"month\" }`."
                                },
                                {
                                    "const": "colorRange",
                                    description: "Paints a gradient / heatmap across a numeric field \u2014 use only when the user asks for a gradient or heatmap. `value`: a colour-range object `{ kind: \"color-range\", mode, min, mid, max }`, where `mode` is `\"number\"` (the default; use `\"percent\"` only for percentage fields or when the user asks) and each of `min`/`mid`/`max` is `{ color, value }`. All three stops are required \u2014 omitting any renders nothing. When the user gives no thresholds, derive them from the field's numeric distribution in the summary: `min.value` from the minimum, `max.value` from the maximum, `mid.value` from the mean (or the midpoint). Pick a three-stop colour ramp running from cool/light (low) to warm/dark (high)."
                                },
                                {
                                    "const": "idDoesNotMatch",
                                    description: "Matches when the entity's id does not equal the rule's `value`. `value`: a non-empty string."
                                },
                                {
                                    "const": "notEqual",
                                    description: "Matches when the field does not equal the rule's `value`. `value`: a string or number."
                                },
                                {
                                    "const": "notContains",
                                    description: "Matches when the text field does not contain the rule's `value` as a substring. `value`: a string."
                                },
                                {
                                    "const": "notBoolean",
                                    description: "Matches when the checkbox field is not ticked. `value`: the literal `true`."
                                },
                                {
                                    "const": "greaterThanOrEqualTo",
                                    description: "Matches when the numeric field is greater than or equal to the rule's `value`. `value`: a number."
                                },
                                {
                                    "const": "lessThanOrEqualTo",
                                    description: "Matches when the numeric field is less than or equal to the rule's `value`. `value`: a number."
                                },
                                {
                                    "const": "between",
                                    description: "Matches when the numeric field is within an inclusive range. `value`: a `[number, number]` pair, `[min, max]`."
                                },
                                {
                                    "const": "notBetween",
                                    description: "Matches when the numeric field is outside the range. `value`: a `[number, number]` pair, `[min, max]`."
                                },
                                {
                                    "const": "dateBetween",
                                    description: "Matches when the date field is within a range. `value`: a two-element array of ISO date strings, `[\"YYYY-MM-DD\", \"YYYY-MM-DD\"]` (start, end)."
                                },
                                {
                                    "const": "dateNotBetween",
                                    description: "Matches when the date field is outside a range. `value`: a two-element array of ISO date strings, `[\"YYYY-MM-DD\", \"YYYY-MM-DD\"]` (start, end)."
                                },
                                {
                                    "const": "dateNotEqual",
                                    description: "Matches when the date field does not equal the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"`."
                                },
                                {
                                    "const": "dateOnOrBefore",
                                    description: "Matches when the date field is on or before the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"`."
                                },
                                {
                                    "const": "dateOnOrAfter",
                                    description: "Matches when the date field is on or after the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"`."
                                },
                                {
                                    "const": "dateRangeOnOrBefore",
                                    description: "Matches when the date-range field ends on or before the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"` or a relative-date object such as `{ \"period\": \"this\", \"unit\": \"month\" }`."
                                },
                                {
                                    "const": "dateRangeOnOrAfter",
                                    description: "Matches when the date-range field starts on or after the rule's `value`. `value`: an ISO date string `\"YYYY-MM-DD\"` or a relative-date object such as `{ \"period\": \"this\", \"unit\": \"month\" }`."
                                },
                                {
                                    "const": "dateRangeBetween",
                                    description: "Matches when the date-range field falls within a range. `value`: a two-element array of ISO date strings, `[\"YYYY-MM-DD\", \"YYYY-MM-DD\"]` (start, end)."
                                },
                                {
                                    "const": "relativeDate",
                                    description: "Matches when the date field falls in a relative period. `value`: a relative-date object such as `{ \"period\": \"last\", \"unit\": \"week\" }` or `{ \"period\": \"inTheNext\", \"unit\": \"day\", \"amount\": 7 }`."
                                },
                                {
                                    "const": "isEmpty",
                                    description: "Matches when the field is empty (has no content). `value`: `null`."
                                },
                                {
                                    "const": "isNotEmpty",
                                    description: "UNSUPPORTED in modern conditional formatting \u2014 do NOT use. For an emptiness check use `isEmpty` instead."
                                },
                                {
                                    "const": "listContains",
                                    description: "Matches when the list field contains at least one of the strings in the rule's `value`. `value`: a non-empty array of strings."
                                },
                                {
                                    "const": "listDoesNotContain",
                                    description: "Matches when the list field contains none of the strings in the rule's `value`. `value`: a non-empty array of strings."
                                }
                            ],
                            description: "How the field is compared against the rule's `value`. The required shape of `value` depends on this comparator \u2014 see each comparator."
                        },
                        name: {
                            type: "string",
                            description: "Name of the field the rule tests (for example `cost` or `lifecycle`), or\nthe literal `\"type\"` to test the entity's own type \u2014 colour a whole type\nwith `name: \"type\"`, `comparator: \"idMatch\"`, and the type name as `value`."
                        },
                        type: {
                            $ref: "#/components/schemas/FilterTypes",
                            description: "`\"attribute\"` for a rule that tests a component or reference field; `\"tag\"` for a tag rule that matches by the tag named in `value`."
                        },
                        value: {
                            $ref: "#/components/schemas/QueryBuilderRuleValue",
                            description: "The value the field is compared against; its required shape is determined by the comparator (see each comparator). For a `tag` rule this is the tag name."
                        },
                        color: {
                            type: "string",
                            description: "The colour painted on the entities this rule matches, as a hex string. Choose from Ardoq's\nconditional-formatting palette, each hue running light \u2192 dark:\nreds/oranges `#FFCFC5 #FF7E64 #ED3915 #A5280F #4B1004`,\ngreens `#BDF5DE #4CE5A6 #09B06B #0F6C45 #03301D`,\nyellows `#FEE685 #FFD230 #FE9A00 #BB4D00 #461901`,\nblues `#BFDBFE #60A5FA #2563EB #1E40AF #172554`,\npurples `#DECDFF #AF73FF #8800FF #4C008F #1F003B`,\npinks `#FFCFFA #FB5FE1 #D623B3 #911775 #4F033C`,\ngreys `#E6E7E9 #A1A3A9 #70737B #43454B #040506`.\nPrefer a hue's mid shade for highlights; when the user names a colour, use the mid shade of the closest hue. Match the user's intent (e.g. red for risk, green for healthy)."
                        },
                        affectsTypeNames: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "The component or reference type(s) the rule is limited to \u2014 e.g.\n`[\"Application\"]`, or `[\"Application\", \"Server\"]` to scope one rule to\nseveral types. Omit to apply to every component or reference regardless of type.\nA pre-existing legacy rule may instead carry a singular `affectsTypeName`;\nwhen it does, reproduce that scope here as a single-element array (e.g.\n`affectsTypeName: \"Application\"` \u2192 `affectsTypeNames: [\"Application\"]`)."
                        },
                        isNegative: {
                            type: "boolean"
                        }
                    },
                    required: [
                        "affectComponent",
                        "affectReference",
                        "name",
                        "type",
                        "value",
                        "color"
                    ],
                    description: "A single conditional-formatting rule. The kind of entity it colours is encoded\nby `affectComponent`, `affectReference`, and `type` together:\n- component rule: `affectComponent: true`, `affectReference: false`, `type: \"attribute\"`;\n- reference rule: `affectComponent: false`, `affectReference: true`, `type: \"attribute\"`;\n- tag rule: `affectComponent: true`, `affectReference: true`, `type: \"tag\"` \u2014 matches every component and reference carrying the tag named in `value`. Set `isNegative: true` to invert it, so it matches every component and reference that does NOT carry the tag (\"without tag X\", \"not tagged X\"). `isNegative` is for tag rules only \u2014 never set it on other rules; negate those with a negated comparator such as `notEqual` or `notContains`. Leave it out otherwise.\n- system-filter rule \u2014 greys out (dims) every component and reference that matches none of the other rules, so only the formatted entities stand out.\n  Include it whenever the user asks to dim, grey out, fade, mute, de-emphasise, or wash out the irrelevant, non-matching, or remaining data, and whenever they ask for the formatted entities to stand out against the rest. It has no effect on its own \u2014 only add it alongside at least one component or reference rule. Leave it out when the user asks for colours only, and drop it from the rule set when the user asks to stop dimming or to restore the other entities' colours.\n  It carries no condition of its own, so emit it verbatim as `{ \"type\": \"system_filter\", \"affectComponent\": true, \"affectReference\": true, \"name\": \"0\", \"value\": \"\", \"color\": \"#D4DAE3\" }`."
                },
                FilterTypes: {
                    oneOf: [
                        {
                            "const": "attribute",
                            description: "A filter type that looks up attributes on a component or reference, or it\ncan be a SpecialAttributeType."
                        },
                        {
                            "const": "tag",
                            description: "A filter type that looks up tags."
                        },
                        {
                            "const": "system_filter"
                        },
                        {
                            "const": "date_range"
                        },
                        {
                            "const": "refLabel"
                        },
                        {
                            "const": "compLabel"
                        }
                    ],
                    description: "`Note that this is not a complete list`"
                },
                QueryBuilderRuleValue: {
                    oneOf: [
                        {
                            type: "null"
                        },
                        {
                            type: "string"
                        },
                        {
                            type: "number"
                        },
                        {
                            type: "boolean"
                        },
                        {
                            type: "array",
                            items: {
                                type: "string"
                            }
                        },
                        {
                            type: "array",
                            prefixItems: [
                                {
                                    type: "number"
                                },
                                {
                                    type: "number"
                                }
                            ],
                            additionalItems: false
                        },
                        {
                            type: "object",
                            properties: {
                                period: {
                                    oneOf: [
                                        {
                                            "const": "this",
                                            description: "The current unit relative to now, e.g. this month."
                                        },
                                        {
                                            "const": "last",
                                            description: "The whole unit before the current one, e.g. last month."
                                        },
                                        {
                                            "const": "next",
                                            description: "The whole unit after the current one, e.g. next month."
                                        }
                                    ],
                                    description: "A whole period relative to now."
                                },
                                unit: {
                                    $ref: "#/components/schemas/RelativeDateUnit",
                                    description: "The unit of time the period spans."
                                }
                            },
                            required: [
                                "period",
                                "unit"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                period: {
                                    oneOf: [
                                        {
                                            "const": "inTheNext",
                                            description: "A forward window spanning `amount` units, starting now."
                                        },
                                        {
                                            "const": "inThePast",
                                            description: "A backward window spanning `amount` units, ending now."
                                        }
                                    ],
                                    description: "The direction of the offset window relative to now."
                                },
                                amount: {
                                    type: "number",
                                    description: "How many `unit`s the window spans."
                                },
                                unit: {
                                    $ref: "#/components/schemas/RelativeDateUnit",
                                    description: "The unit of time each step of `amount` spans."
                                }
                            },
                            required: [
                                "period",
                                "amount",
                                "unit"
                            ]
                        },
                        {
                            $ref: "#/components/schemas/ListFieldFormatting"
                        },
                        {
                            $ref: "#/components/schemas/ColorRangeFormatting"
                        }
                    ],
                    description: "The `value` field in filter entities.\nNot all filter types can have all of these, so this needs to be cleaned up and narrowed down to the\nspecific types allowed for conditional formatting v0, conditional formatting v1, advanced search, etc."
                },
                RelativeDateUnit: {
                    oneOf: [
                        {
                            "const": "day"
                        },
                        {
                            "const": "week"
                        },
                        {
                            "const": "month"
                        },
                        {
                            "const": "quarter"
                        },
                        {
                            "const": "year"
                        }
                    ]
                },
                ListFieldFormatting: {
                    type: "object",
                    properties: {
                        "No value": {
                            $ref: "#/components/schemas/ColorFormatting"
                        }
                    },
                    required: [
                        "No value"
                    ],
                    description: "Colours a list/select field by its values: a map from each list value to its\n`ColorFormatting`. Colours every value of one field in a single rule, rather\nthan one rule per value. The `\"No value\"` key colours entities that have no\nvalue for the field and must always be present.",
                    additionalProperties: {
                        $ref: "#/components/schemas/ColorFormatting"
                    }
                },
                ColorFormatting: {
                    type: "object",
                    properties: {
                        color: {
                            type: "string",
                            description: "A hex colour from Ardoq's conditional-formatting palette, same palette as a rule's top-level `color`."
                        },
                        isDisabled: {
                            type: "boolean",
                            description: "`true` to hide (not colour) entities with this value."
                        }
                    },
                    required: [
                        "color",
                        "isDisabled"
                    ],
                    description: "The formatting applied to a single value of a list/select field within a\nlist-field colour map."
                },
                ColorRangeFormatting: {
                    type: "object",
                    properties: {
                        kind: {
                            "const": "color-range"
                        },
                        mode: {
                            $ref: "#/components/schemas/ColorRangeType"
                        },
                        min: {
                            $ref: "#/components/schemas/ColorRangePoint"
                        },
                        mid: {
                            $ref: "#/components/schemas/ColorRangeMidPoint"
                        },
                        max: {
                            $ref: "#/components/schemas/ColorRangePoint"
                        }
                    },
                    required: [
                        "kind",
                        "mode",
                        "min",
                        "mid",
                        "max"
                    ],
                    description: "A numeric gradient (heatmap): colours entities along a min \u2192 mid \u2192 max scale\nby the value of a numeric field."
                },
                ColorRangeType: {
                    oneOf: [
                        {
                            "const": "number"
                        },
                        {
                            "const": "percent"
                        },
                        {
                            "const": "percentile"
                        }
                    ]
                },
                ColorRangePoint: {
                    type: "object",
                    properties: {
                        value: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "number"
                                }
                            ]
                        },
                        color: {
                            type: "string"
                        }
                    },
                    required: [
                        "value",
                        "color"
                    ]
                },
                ColorRangeMidPoint: {
                    type: "object",
                    properties: {
                        value: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "number"
                                }
                            ]
                        },
                        color: {
                            type: "string"
                        }
                    },
                    required: [
                        "color"
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ApplyConditionalFormattingPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: FORMATTING_CONTEXT_DESCRIPTION,
    isContext: isApplyConditionalFormattingContext,
    validate: (rawPayload, { currentRules, summary, componentTypeNamesWithAncestors, availableTagNames, }, executionState) => {
        if (!(() => { const _iv2 = new Set(["idMatch", "dateMatch", "dateEarlier", "dateLater", "boolean", "equals", "lessThan", "greaterThan", "contains", "dateRangeContains", "dateRangeDoesNotContain", "dateRangeBefore", "dateRangeAfter", "colorRange", "idDoesNotMatch", "notEqual", "notContains", "notBoolean", "greaterThanOrEqualTo", "lessThanOrEqualTo", "between", "notBetween", "dateBetween", "dateNotBetween", "dateNotEqual", "dateOnOrBefore", "dateOnOrAfter", "dateRangeOnOrBefore", "dateRangeOnOrAfter", "dateRangeBetween", "relativeDate", "isEmpty", "isNotEmpty", "listContains", "listDoesNotContain"]); const _ip0 = (input: any) => {
            const array = input;
            const tuplePredicators = [
                [
                    (top: any[]): any => top.length === 2 && "number" === typeof top[0] && "number" === typeof top[1],
                    (entire: any[]): any => entire.length === 2 && "number" === typeof entire[0] && "number" === typeof entire[1]
                ] as const
            ];
            for (const pred of tuplePredicators)
                if (pred[0](array))
                    return pred[1](array);
            const top = input[0];
            if (0 === input.length)
                return true;
            const arrayPredicators = [
                [
                    (top: any[]): any => "string" === typeof top,
                    (entire: any[]): any => entire.every((elem: any) => "string" === typeof elem)
                ] as const
            ];
            const passed = arrayPredicators.filter((pred: any) => pred[0](top));
            if (1 === passed.length)
                return passed[0]![1](array);
            else if (1 < passed.length)
                for (const pred of passed)
                    if (array.every((value: any) => true === pred[0](value)))
                        return pred[1](array);
            return false;
        }; const _io0 = (input: any): boolean => Array.isArray(input.rules) && input.rules.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)); const _io1 = (input: any): boolean => "boolean" === typeof input.affectComponent && "boolean" === typeof input.affectReference && (undefined === input.comparator || true === _iv2.has(input.comparator)) && "string" === typeof input.name && ("attribute" === input.type || "tag" === input.type || "system_filter" === input.type || "date_range" === input.type || "refLabel" === input.type || "compLabel" === input.type) && (undefined !== input.value && (null === input.value || "string" === typeof input.value || "number" === typeof input.value || "boolean" === typeof input.value || (Array.isArray(input.value) && (_ip0(input.value) || false) || "object" === typeof input.value && null !== input.value && _iu0(input.value)))) && "string" === typeof input.color && (undefined === input.affectsTypeNames || Array.isArray(input.affectsTypeNames) && input.affectsTypeNames.every((elem: any) => "string" === typeof elem)) && (undefined === input.isNegative || "boolean" === typeof input.isNegative); const _io2 = (input: any): boolean => "object" === typeof input["No value"] && null !== input["No value"] && _io3(input["No value"]) && Object.keys(input).every((key: any) => {
            if (["No value"].some((prop: any) => key === prop))
                return true;
            const value = input[key];
            if (undefined === value)
                return true;
            return undefined === value || "object" === typeof value && null !== value && _io3(value);
        }); const _io3 = (input: any): boolean => "string" === typeof input.color && "boolean" === typeof input.isDisabled; const _io4 = (input: any): boolean => "color-range" === input.kind && ("number" === input.mode || "percent" === input.mode || "percentile" === input.mode) && ("object" === typeof input.min && null !== input.min && _io5(input.min)) && ("object" === typeof input.mid && null !== input.mid && _io6(input.mid)) && ("object" === typeof input.max && null !== input.max && _io5(input.max)); const _io5 = (input: any): boolean => (null === input.value || "number" === typeof input.value) && "string" === typeof input.color; const _io6 = (input: any): boolean => (null === input.value || undefined === input.value || "number" === typeof input.value) && "string" === typeof input.color; const _io7 = (input: any): boolean => ("this" === input.period || "last" === input.period || "next" === input.period) && ("day" === input.unit || "week" === input.unit || "month" === input.unit || "quarter" === input.unit || "year" === input.unit); const _io8 = (input: any): boolean => ("inTheNext" === input.period || "inThePast" === input.period) && "number" === typeof input.amount && ("day" === input.unit || "week" === input.unit || "month" === input.unit || "quarter" === input.unit || "year" === input.unit); const _iu0 = (input: any): any => (() => {
            if (undefined !== input["No value"])
                return _io2(input);
            else if (undefined !== input.kind)
                return _io4(input);
            else if ("this" === input.period || "last" === input.period || "next" === input.period)
                return _io7(input);
            else if ("inTheNext" === input.period || "inThePast" === input.period)
                return _io8(input);
            else
                return false;
        })(); return (input: any): input is ApplyConditionalFormattingPayload => "object" === typeof input && null !== input && _io0(input); })()(rawPayload)) {
            return {
                status: 'rejected',
                reason: 'The AI returned invalid conditional formatting.',
            };
        }
        const invalidRule = validateApplyConditionalFormattingPayload(rawPayload, {
            summary,
            currentRules,
            componentTypeNamesWithAncestors,
            availableTagNames,
        });
        if (invalidRule) {
            return toCommandRejection({
                kind: invalidRule.kind,
                buildReason: showName => buildReasonMessage(showInvalidRuleNames(invalidRule, showName)),
            });
        }
        return {
            status: 'valid',
            payload: {
                rules: normalizeConditionalFormattingRules(rawPayload.rules, summary, executionState),
            },
        };
    },
    execute: executeResolvedApplyConditionalFormatting,
    isApplied: ({ rules }, { currentRules }) => isEqual(currentRules, asStoredByTheFilters(rules)),
});
